import React from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "@mui/material/styles";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { PickersCalendarHeader } from "@mui/x-date-pickers/PickersCalendarHeader";
import { PickersDay } from "@mui/x-date-pickers/PickersDay";
import { getDayOfYear, isSameDay, isWeekend } from "date-fns";
import enLocale from "date-fns/locale/en-US";
import huLocale from "date-fns/locale/hu";

export interface OcDatePickerProps {
  error?: boolean;
  helperText?: string;
  id: string;
  label?: string;
  minDate?: Date;
  maxDate?: Date;
  onChange: (date: Date | null) => void;
  onMonthChange?: (date: Date) => void;
  format: string;
  value: Date | null;
  disabled?: boolean;
  contractCalendar?: string;
  onFocus?: () => void;
  name?: string;
}

export const OcDatePicker: React.FC<OcDatePickerProps> = ({
  error,
  helperText,
  id,
  label,
  minDate,
  maxDate,
  onChange,
  onMonthChange,
  format,
  value,
  disabled = false,
  contractCalendar,
  onFocus,
  name,
}) => {
  const { i18n } = useTranslation();
  const theme = useTheme();

  let lang: "en" | "hu" =
    i18n && i18n.language
      ? (i18n.language.substr(0, 2).toLowerCase() as "en" | "hu")
      : "hu";

  const getLocaleMap = () => {
    const localeMap = {
      en: enLocale,
      hu: huLocale,
    };
    return localeMap[lang] || huLocale;
  };

  return (
    <LocalizationProvider
      dateAdapter={AdapterDateFns}
      adapterLocale={getLocaleMap()}
    >
      <DatePicker
        minDate={minDate}
        maxDate={maxDate}
        label={label}
        value={value}
        format={format}
        onChange={onChange}
        onMonthChange={onMonthChange}
        disabled={disabled}
        desktopModeMediaQuery="@media (min-width: 0px)"
        slots={{
          calendarHeader: (calendarHeaderProps) => (
            <PickersCalendarHeader
              {...calendarHeaderProps}
              data-testid={`date-picker-header-${id}`}
            />
          ),
          day: (props) => {
            const { day, selected } = props;
            let isToday = isSameDay(day, new Date());
            let dayOfYear = getDayOfYear(day);
            let dayState = contractCalendar?.charAt(dayOfYear - 1);
            let bgColor = "";
            let textColor = "";

            if (dayState === "P" || dayState === "T" || (isWeekend(day) && dayState !== ".")) {
              bgColor = theme.palette.gray20.main;
            } else if (dayState === "F") {
              textColor = theme.palette.primary.main;
            }

            if (isToday) {
              bgColor = "#f5d2da";
            }

            if (selected) {
              bgColor = theme.palette.primary.main;
              textColor = theme.palette.white.main;
            }

            return (
              <PickersDay
                {...props}
                data-timestamp={day.getTime().toString()}
                data-testid={`date-picker-day-${day.getTime()}`}
                sx={{
                  color: textColor,
                  backgroundColor: bgColor,
                  borderRadius: "0px",
                  "&:hover": {
                    backgroundColor: theme.palette.gray10.main,
                  },
                }}
              />
            );
          },
        }}
        slotProps={{
          calendarHeader: {
            format: "yyyy MMMM",
          },
          textField: {
            id: `${id}-${format}`,
            name,
            error: !!error,
            helperText: helperText,
            variant: "outlined",
            fullWidth: true,
            sx: {
              backgroundColor: theme.palette.white.main,
            },
            onFocus,
            inputProps: {
              "data-testid": `date-picker-input-${id}`,
            },
          },
          actionBar: {
            actions: [],
          },
          openPickerButton: {
            "data-testid": `date-picker-calendar-button-${id}`,
            sx: {
              backgroundColor: theme.palette.gray20.main,
              marginRight: "2px",
              padding: "12px",
            },
          } as any,
        }}
      />
    </LocalizationProvider>
  );
};

export default OcDatePicker;
